// ============================================================
// RmkGameEndModal — 루미큐브 라운드/매치 종료 정산 모달  (SEQ-10214 S5c)
//   라운드 종료: RoundEnded 페이로드 (만치카 점수, cumulativeAfter)
//   매치 종료: GameEnded 페이로드 (finalScores, finalRank)
//   TtrGameEndModal 패턴 정합 (overlay + table)
// ============================================================

import React from 'react'
import { RmkRoundScore, RmkFinalScore, RmkPlayer } from '../store/rummikub'

export interface RmkGameEndModalProps {
  // 라운드 종료 (SCORING 상태 — 다음 라운드 가능)
  roundScore?:  RmkRoundScore
  // 매치 종료 (ENDED 상태)
  finalScore?:  RmkFinalScore
  players:      RmkPlayer[]
  myUserCode:   number | null
  // 라운드 종료 후 계속 게임이 있을 때
  onNextRound?: () => void
  // 게임 완전 종료
  onClose:      () => void
}

const RmkGameEndModal: React.FC<RmkGameEndModalProps> = ({
  roundScore,
  finalScore,
  players,
  myUserCode,
  onNextRound,
  onClose,
}) => {
  const isGameEnded = !!finalScore

  function nickOf(userCode: number): string {
    return players.find(p => p.userCode === userCode)?.nickname ?? `#${userCode}`
  }

  return (
    <div
      className="rmk-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) { if (!isGameEnded) onClose(); } }}
      role="presentation"
    >
      <div className="rmk-modal" role="dialog" aria-label={isGameEnded ? '게임 종료' : '라운드 종료'}>
        <h2 className="rmk-modal-title">
          {isGameEnded ? '게임 종료' : '라운드 종료'}
        </h2>

        {/* 라운드 점수 표 */}
        {roundScore && (
          <>
            <h3>라운드 {roundScore.roundNo} 결과 — 만치카 점수</h3>
            <table className="rmk-score-table">
              <thead>
                <tr>
                  <th>플레이어</th>
                  <th>손패 합</th>
                  <th>라운드 점수</th>
                  <th>누적 점수</th>
                </tr>
              </thead>
              <tbody>
                {roundScore.scores.map(s => (
                  <tr
                    key={s.userCode}
                    className={
                      s.isWinner ? 'rmk-score-winner' :
                      s.userCode === myUserCode ? 'rmk-score-me' : ''
                    }
                  >
                    <td>
                      {nickOf(s.userCode)}
                      {s.isWinner && ' 🏆'}
                      {s.userCode === myUserCode && ' (나)'}
                    </td>
                    <td>{s.handPenalty > 0 ? `${s.handPenalty}` : '0'}</td>
                    <td className={s.roundScore < 0 ? 'rmk-score-negative' : 'rmk-score-zero'}>
                      {s.roundScore > 0 ? `+${s.roundScore}` : s.roundScore}
                    </td>
                    <td>{s.cumulativeAfter}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* 최종 점수 표 (매치 종료) */}
        {finalScore && (
          <>
            <h3>최종 순위</h3>
            <table className="rmk-score-table">
              <thead>
                <tr>
                  <th>순위</th>
                  <th>플레이어</th>
                  <th>누적 점수</th>
                  <th>라운드 1위</th>
                </tr>
              </thead>
              <tbody>
                {[...finalScore.finalScores]
                  .sort((a, b) => a.finalRank - b.finalRank)
                  .map(s => (
                    <tr
                      key={s.userCode}
                      className={
                        s.userCode === finalScore.winnerUserCode ? 'rmk-score-winner' :
                        s.userCode === myUserCode ? 'rmk-score-me' : ''
                      }
                    >
                      <td>{s.finalRank}위</td>
                      <td>
                        {nickOf(s.userCode)}
                        {s.userCode === finalScore.winnerUserCode && ' 🏆'}
                        {s.userCode === myUserCode && ' (나)'}
                      </td>
                      <td>{s.cumulativeScore}</td>
                      <td>{s.round1stCount}회</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </>
        )}

        {/* 버튼 */}
        <div className="rmk-modal-actions">
          {!isGameEnded && onNextRound && (
            <button className="rmk-btn rmk-btn-next" onClick={onNextRound}>
              다음 라운드
            </button>
          )}
          <button className="rmk-btn rmk-btn-close" onClick={onClose}>
            {isGameEnded ? '나가기' : '닫기'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RmkGameEndModal
